// ============================================================
// PariteFusion.jsx — Service "Parité de fusion" (cas pratique n°1)
// Calcul de parité d'échange en fusion-absorption, approche multicritères
// (position-recommandation AMF n° 2011-11, prolongeant la recommandation
// COB de 1977). Onglet du Dash, monté sous <FormScope id="parite">.
// Données client 100 % session-only (useSessionState) — rien sur disque.
// Tout le calcul est front (arithmétique pure), aucun appel réseau.
// ============================================================

import React, { useMemo } from "react";
import { P } from "./palette";
import { useSessionState } from "./sessionForm";
import { locOf, fmtDec, fmtInt, fmtEuro, fmtMoney, fmtPct } from "./format";
import { buildMemoPdf, downloadPdf, MemoPaper } from "./memoDoc";
import { inS, lbS, thS, tdS, NumIn, SecT, Metric, WarnLine } from "./caseUi";

// ── Libellés internes bilingues (seul grp.parite vit dans TR d'App.jsx) ──
const T = {
  fr: {
    intro: "Calcul de parité d'échange en fusion-absorption — approche multicritères, homogénéité des méthodes entre les deux sociétés, critères écartés motivés, parité arrêtée par un jugement motivé (position-recommandation AMF n° 2011-11, prolongeant la recommandation COB de 1977).",
    s1: "Sociétés en présence", s2: "Critères de valorisation", s3: "Fourchette de parités",
    s4: "Parité retenue", s5: "Rapport d'échange et soulte",
    absorbante: "A — Absorbante", absorbee: "B — Absorbée",
    denomination: "Dénomination", denomPh: "ex. Alpha SA", nbActions: "Nombre d'actions",
    critHint: "Homogénéité imposée : un critère s'applique aux deux sociétés ou est écarté avec motif. Un critère retenu à moitié saisi est signalé et exclu du calcul.",
    colCrit: "Critère", colUnit: "Unité de saisie", colValA: "Valeur A", colValB: "Valeur B",
    colVpaA: "v/action A", colVpaB: "v/action B", colParite: "Parité (A pour 1 B)", colPoids: "Poids",
    uGlobale: "Valeur globale (€)", uParAction: "Par action (€/action)",
    phGlobale: "ex. 120M", phParAction: "ex. 150,00",
    retenu: "Retenu", ecarte: "Écarté", delCrit: "Retirer ce critère",
    motif: "Motif d'écartement", motifPh: "Pourquoi ce critère n'est pas retenu (exigence AMF)…",
    motifMissing: "Motif d'écartement à renseigner (exigence AMF)",
    incomplete: "Critère incomplet (une seule valeur saisie) — exclu du calcul de la fourchette",
    needShares: "Nombre d'actions requis (section 1) pour convertir la valeur globale en valeur par action",
    addCrit: "+ Ajouter un critère",
    nRetenus: (n) => `${n} critère${n > 1 ? "s" : ""} retenu${n > 1 ? "s" : ""}`,
    noRetained: "Renseignez au moins un critère complet (valeurs A et B) pour calculer la fourchette.",
    min: "Parité min", max: "Parité max", moy: "Moyenne simple", pond: "Moyenne pondérée",
    pondMark: "pondérée", retMark: "retenue",
    retVal: "Parité retenue", takePond: (v) => `Reprendre la moyenne pondérée (${v})`,
    inRange: "dans la fourchette", outRange: "hors fourchette — l'écart doit être explicitement justifié",
    ecartPond: (e) => `écart de ${e} par rapport à la moyenne pondérée`,
    justif: "Justification de la parité retenue",
    justifPh: "Motivez la parité retenue : proximité de la moyenne pondérée, poids donné à certains critères, éléments de contexte… (repris tel quel dans le mémo)",
    rv: "Rapport de valeurs",
    rvText: (x) => `1 action B (absorbée) = ${x} action(s) A (absorbante)`,
    re: "Rapport d'échange",
    reText: (n, m) => `${n} action${n > 1 ? "s" : ""} A remise${n > 1 ? "s" : ""} pour ${m} action${m > 1 ? "s" : ""} B apportée${m > 1 ? "s" : ""}`,
    sugg: "Rapports praticables suggérés (numérateur et dénominateur ≤ 12)",
    suggNeedsRet: "Saisissez la parité retenue (section 4) pour obtenir des suggestions de rapport praticable.",
    ecartLbl: "écart",
    nbA: "Actions A remises (N)", nbB: "Actions B apportées (M)",
    valRef: "Valeur de référence action A", valRefAuto: (v) => `Préremplie : moyenne pondérée des valeurs par action de A (${v}) — modifiable`,
    nominal: "Nominal action A (optionnel)", nominalHint: "Sert au contrôle du plafond légal de la soulte",
    titresAttr: "Titres attribués en rémunération",
    titresAttrAuto: (v) => `Prérempli : actions créées théoriques (${v}) — à ajuster en cas d'auto-détention, de fusion-renonciation ou de fusion simplifiée (l'assiette légale est le nominal des titres effectivement attribués en rémunération de l'apport)`,
    crees: "Actions A créées (théorique)", pariteImpl: "Parité implicite N/M",
    soulteAct: "Soulte / action B", soulteTot: "Soulte totale",
    rompus: (m) => `Le nombre d'actions B n'est pas divisible par ${m} : des rompus subsisteront (à traiter par achat/vente de droits).`,
    soulteNone: "Rapport d'échange aligné sur la parité retenue — aucune soulte.",
    soulteNeg: "Le rapport d'échange sert les actionnaires de B au-delà de la parité retenue — pas de soulte, la dilution est supportée par les actionnaires de A. Choisissez un rapport inférieur ou justifiez ce choix.",
    ctrlOk: (s, p) => `Contrôle de la soulte (C. com., art. L. 236-1) : ${s} ≤ 10 % de la valeur nominale des titres attribués en rémunération de l'apport (plafond ${p}) — conforme.`,
    ctrlKo: (s, p) => `Contrôle de la soulte (C. com., art. L. 236-1) : ${s} > 10 % de la valeur nominale des titres attribués en rémunération de l'apport (plafond ${p}) — l'opération excède le plafond légal de la soulte et perd la qualification de fusion.`,
  },
  en: {
    intro: "Merger exchange-ratio computation — multi-criteria approach, homogeneous methods across both companies, discarded criteria justified, final ratio set by reasoned judgement (AMF position-recommendation No. 2011-11, extending the 1977 COB recommendation).",
    s1: "Companies", s2: "Valuation criteria", s3: "Exchange-ratio range",
    s4: "Retained ratio", s5: "Practical exchange ratio and cash adjustment",
    absorbante: "A — Absorbing", absorbee: "B — Absorbed",
    denomination: "Name", denomPh: "e.g. Alpha SA", nbActions: "Number of shares",
    critHint: "Homogeneity enforced: a criterion applies to both companies or is discarded with a reason. A half-filled retained criterion is flagged and excluded from the computation.",
    colCrit: "Criterion", colUnit: "Input unit", colValA: "Value A", colValB: "Value B",
    colVpaA: "v/share A", colVpaB: "v/share B", colParite: "Ratio (A per 1 B)", colPoids: "Weight",
    uGlobale: "Aggregate value (€)", uParAction: "Per share (€/share)",
    phGlobale: "e.g. 120M", phParAction: "e.g. 150.00",
    retenu: "Retained", ecarte: "Discarded", delCrit: "Remove this criterion",
    motif: "Reason for discarding", motifPh: "Why this criterion is not retained (AMF requirement)…",
    motifMissing: "Reason for discarding required (AMF requirement)",
    incomplete: "Incomplete criterion (only one value entered) — excluded from the range computation",
    needShares: "Number of shares required (section 1) to convert the aggregate value into a per-share value",
    addCrit: "+ Add a criterion",
    nRetenus: (n) => `${n} retained criteri${n > 1 ? "a" : "on"}`,
    noRetained: "Fill in at least one complete criterion (values A and B) to compute the range.",
    min: "Min ratio", max: "Max ratio", moy: "Simple average", pond: "Weighted average",
    pondMark: "weighted", retMark: "retained",
    retVal: "Retained ratio", takePond: (v) => `Use the weighted average (${v})`,
    inRange: "within the range", outRange: "outside the range — the deviation must be explicitly justified",
    ecartPond: (e) => `${e} deviation from the weighted average`,
    justif: "Justification of the retained ratio",
    justifPh: "Justify the retained ratio: proximity to the weighted average, weight given to certain criteria, context… (quoted verbatim in the memo)",
    rv: "Value ratio",
    rvText: (x) => `1 share B (absorbed) = ${x} share(s) A (absorbing)`,
    re: "Exchange ratio",
    reText: (n, m) => `${n} share${n > 1 ? "s" : ""} A issued for ${m} share${m > 1 ? "s" : ""} B contributed`,
    sugg: "Suggested practical ratios (numerator and denominator ≤ 12)",
    suggNeedsRet: "Enter the retained ratio (section 4) to get practical ratio suggestions.",
    ecartLbl: "deviation",
    nbA: "Shares A issued (N)", nbB: "Shares B contributed (M)",
    valRef: "Reference value of share A", valRefAuto: (v) => `Pre-filled: weighted average per-share value of A (${v}) — editable`,
    nominal: "Par value of share A (optional)", nominalHint: "Used for the legal cash-adjustment cap check",
    titresAttr: "Shares allotted as consideration",
    titresAttrAuto: (v) => `Pre-filled: theoretical shares created (${v}) — adjust for treasury shares, allotment waivers or simplified mergers (the legal base is the par value of the shares actually allotted as consideration for the contribution)`,
    crees: "Shares A created (theoretical)", pariteImpl: "Implied ratio N/M",
    soulteAct: "Cash adj. / share B", soulteTot: "Total cash adjustment",
    rompus: (m) => `The number of B shares is not divisible by ${m}: fractional entitlements will remain (to be settled by buying/selling rights).`,
    soulteNone: "Exchange ratio aligned with the retained ratio — no cash adjustment.",
    soulteNeg: "The exchange ratio serves B shareholders beyond the retained ratio — no cash adjustment; the dilution is borne by A shareholders. Choose a lower ratio or justify this choice.",
    ctrlOk: (s, p) => `Cash-adjustment check (French Com. Code, art. L. 236-1): ${s} ≤ 10% of the par value of the shares allotted as consideration for the contribution (cap ${p}) — compliant.`,
    ctrlKo: (s, p) => `Cash-adjustment check (French Com. Code, art. L. 236-1): ${s} > 10% of the par value of the shares allotted as consideration for the contribution (cap ${p}) — the transaction exceeds the legal cap and loses its merger qualification.`,
  },
};

const gcd = (a, b) => (b ? gcd(b, a % b) : a);

// ══════════════════════════════════════════════════════════════════════════
// MÉMO JUSTIFICATIF (lot 2) — template déterministe : le mémo n'assemble QUE
// les saisies de l'évaluateur (exigence de traçabilité, aucun contenu généré).
// Un modèle de blocs unique (buildMemoModel) alimente l'aperçu écran ET
// l'export PDF : le livrable est exactement ce qui est prévisualisé.
// ══════════════════════════════════════════════════════════════════════════

const MEMO_T = {
  fr: {
    tabCalc: "Calcul et parité", tabMemo: "Mémo justificatif",
    ckTitle: "Complétude du mémo",
    ckSoc: "Sociétés identifiées (dénominations et nombres d'actions)",
    ckCrit: "Au moins un critère retenu complet",
    ckMotifs: "Motif renseigné pour chaque critère écarté",
    ckComplets: "Aucun critère retenu laissé vide ou incomplet (compléter, écarter ou supprimer)",
    ckJustif: "Parité retenue saisie et justifiée",
    ckRatio: "Rapport d'échange défini (N et M)",
    ckNominal: "Nominal de l'action A non renseigné — le contrôle L. 236-1 figurera comme « non effectué » dans le mémo",
    exportBtn: "Exporter le mémo (PDF)",
    exportBlocked: "Complétez les points signalés ci-dessus pour activer l'export",
    memoTitle: "MÉMORANDUM", memoSub: "Détermination de la parité d'échange",
    op: (A, B) => `Fusion-absorption de ${B} (absorbée) par ${A} (absorbante)`,
    socLine: (n, sh) => `${n} — ${sh} actions`,
    dateLbl: "Établi le",
    s1: "1. Opération", s2: "2. Cadre de référence", s3: "3. Démarche multicritères",
    s4: "4. Critères retenus", s5: "5. Critères écartés", s6: "6. Fourchette des parités",
    s7: "7. Parité retenue", s8: "8. Rapport d'échange et soulte",
    cadre: "Conformément à la position-recommandation AMF n° 2011-11, qui prolonge la recommandation COB de 1977, la parité d'échange est appréciée selon une approche multicritères : les critères d'évaluation sont appliqués de manière homogène aux deux sociétés, les critères non retenus sont écartés de manière motivée, et la parité retenue procède d'un jugement motivé prenant appui sur la fourchette des parités obtenues — non d'une formule mécanique.",
    demarche: (x, r, e) => `${x} critères d'évaluation ont été examinés : ${r} retenu(s) et appliqué(s) de manière homogène aux deux sociétés, ${e} écarté(s) de manière motivée (section 5).`,
    demarcheNoEc: (x, r) => `${x} critères d'évaluation ont été examinés et ${r} retenu(s), appliqué(s) de manière homogène aux deux sociétés. Aucun critère n'a été écarté.`,
    thCrit: "Critère", thUnit: "Unité", thVA: "Valeur A", thVB: "Valeur B", thvA: "v/action A", thvB: "v/action B", thPar: "Parité (A/1 B)",
    uniteG: "globale", uniteP: "par action",
    poidsNote: (s) => `Pondérations retenues pour la moyenne pondérée : ${s}.`,
    aucunEcarte: "Aucun critère écarté.",
    ecarteLine: (l, m) => `${l} — motif : ${m}`,
    fourch: (min, max, moy, pond) => `Les critères retenus font ressortir une fourchette de parités comprise entre ${min} et ${max} (en nombre d'actions A pour une action B), avec une moyenne simple de ${moy} et une moyenne pondérée de ${pond}.`,
    retRv: (v) => `Rapport de valeurs : 1 action B (absorbée) = ${v} action(s) A (absorbante).`,
    retEcart: (inR, e) => `La parité retenue ${inR ? "s'inscrit dans la fourchette des critères" : "se situe hors de la fourchette des critères"} et présente un écart de ${e} par rapport à la moyenne pondérée.`,
    retJust: "Justification de l'évaluateur :",
    re1: (r, n, m, pi, e) => `La parité retenue de ${r} ne correspondant pas à un rapport d'échange praticable en nombres entiers, le rapport retenu est de ${n} action(s) A remise(s) pour ${m} action(s) B apportée(s), soit une parité implicite de ${pi} (écart de ${e} par rapport à la parité retenue).`,
    re1Exact: (r, n, m) => `La parité retenue de ${r} correspond exactement au rapport d'échange de ${n} action(s) A remise(s) pour ${m} action(s) B apportée(s).`,
    reAttr: (nb) => `Titres attribués en rémunération de l'apport : ${nb} actions A.`,
    reAttrAdj: (nb, th) => `Titres attribués en rémunération de l'apport : ${nb} actions A, contre ${th} actions théoriquement créées (ajustement saisi par l'évaluateur : auto-détention, renonciation ou fusion simplifiée).`,
    reSoulteCalc: (r, pi, vr, sa, src) => `L'écart entre la parité retenue et la parité implicite du rapport d'échange est compensé par une soulte en numéraire : (${r} - ${pi}) × ${vr} (valeur de référence de l'action A, ${src}) = ${sa} par action B apportée.`,
    valRefSrcAuto: "moyenne pondérée des valeurs par action de A issues des critères retenus",
    valRefSrcSaisie: "valeur retenue par l'évaluateur",
    reSoulteTot: (st, nb) => `Soulte totale : ${st} pour ${nb} actions B apportées.`,
    reNoSoulte: "Le rapport d'échange est aligné sur la parité retenue : aucune soulte n'est due.",
    reNegSoulte: "Le rapport d'échange sert les actionnaires de B au-delà de la parité retenue ; aucune soulte n'est due, la différence étant supportée par les actionnaires de A.",
    reAssiette: (nb, nom, base, p) => `Assiette du contrôle (C. com., art. L. 236-1) : ${nb} titres attribués en rémunération de l'apport × ${nom} de valeur nominale = ${base} ; plafond légal de la soulte : 10 % de cette assiette, soit ${p}.`,
    reCtrlOk: (st, p) => `La soulte totale (${st}) n'excède pas ce plafond (${p}) : l'opération demeure dans la limite fixée par l'article L. 236-1 du code de commerce.`,
    reCtrlKo: (st, p) => `La soulte totale (${st}) excède ce plafond (${p}) : l'opération dépasse la limite fixée par l'article L. 236-1 du code de commerce.`,
    reCtrlNa: "Le contrôle du plafond légal de la soulte (C. com., art. L. 236-1) n'a pas été effectué, la valeur nominale de l'action A n'ayant pas été renseignée.",
    reRompus: (m) => `Le nombre d'actions B n'étant pas divisible par ${m}, des rompus subsisteront et devront être traités (achat ou vente de droits).`,
    mention: "Document établi exclusivement à partir des données et justifications saisies par l'évaluateur. Aucune donnée de marché ni aucun contenu n'a été généré automatiquement.",
    footer: "Hard to Value — Mémo de parité",
  },
  en: {
    tabCalc: "Computation and ratio", tabMemo: "Justification memo",
    ckTitle: "Memo completeness",
    ckSoc: "Companies identified (names and share counts)",
    ckCrit: "At least one complete retained criterion",
    ckMotifs: "Reason given for every discarded criterion",
    ckComplets: "No retained criterion left empty or incomplete (fill in, discard or remove)",
    ckJustif: "Retained ratio entered and justified",
    ckRatio: "Exchange ratio defined (N and M)",
    ckNominal: "Par value of share A not entered — the L. 236-1 check will appear as \"not performed\" in the memo",
    exportBtn: "Export the memo (PDF)",
    exportBlocked: "Complete the flagged items above to enable the export",
    memoTitle: "MEMORANDUM", memoSub: "Determination of the exchange ratio",
    op: (A, B) => `Merger-absorption of ${B} (absorbed) by ${A} (absorbing)`,
    socLine: (n, sh) => `${n} — ${sh} shares`,
    dateLbl: "Prepared on",
    s1: "1. Transaction", s2: "2. Framework", s3: "3. Multi-criteria approach",
    s4: "4. Retained criteria", s5: "5. Discarded criteria", s6: "6. Range of ratios",
    s7: "7. Retained ratio", s8: "8. Exchange ratio and cash adjustment",
    cadre: "In accordance with AMF position-recommendation No. 2011-11, extending the 1977 COB recommendation, the exchange ratio is assessed under a multi-criteria approach: valuation criteria are applied homogeneously to both companies, criteria not retained are discarded with stated reasons, and the retained ratio results from a reasoned judgement grounded in the range of ratios obtained — not from a mechanical formula.",
    demarche: (x, r, e) => `${x} valuation criteria were examined: ${r} retained and applied homogeneously to both companies, ${e} discarded with stated reasons (section 5).`,
    demarcheNoEc: (x, r) => `${x} valuation criteria were examined and ${r} retained, applied homogeneously to both companies. No criterion was discarded.`,
    thCrit: "Criterion", thUnit: "Unit", thVA: "Value A", thVB: "Value B", thvA: "v/share A", thvB: "v/share B", thPar: "Ratio (A/1 B)",
    uniteG: "aggregate", uniteP: "per share",
    poidsNote: (s) => `Weights used for the weighted average: ${s}.`,
    aucunEcarte: "No criterion was discarded.",
    ecarteLine: (l, m) => `${l} — reason: ${m}`,
    fourch: (min, max, moy, pond) => `The retained criteria yield a range of exchange ratios between ${min} and ${max} (shares A per share B), with a simple average of ${moy} and a weighted average of ${pond}.`,
    retRv: (v) => `Value ratio: 1 share B (absorbed) = ${v} share(s) A (absorbing).`,
    retEcart: (inR, e) => `The retained ratio ${inR ? "falls within the range of the criteria" : "falls outside the range of the criteria"} and deviates by ${e} from the weighted average.`,
    retJust: "Valuer's justification:",
    re1: (r, n, m, pi, e) => `As the retained ratio of ${r} does not correspond to a practical whole-number exchange ratio, the ratio adopted is ${n} share(s) A issued for ${m} share(s) B contributed, i.e. an implied ratio of ${pi} (a ${e} deviation from the retained ratio).`,
    re1Exact: (r, n, m) => `The retained ratio of ${r} corresponds exactly to the exchange ratio of ${n} share(s) A issued for ${m} share(s) B contributed.`,
    reAttr: (nb) => `Shares allotted as consideration for the contribution: ${nb} shares A.`,
    reAttrAdj: (nb, th) => `Shares allotted as consideration for the contribution: ${nb} shares A, versus ${th} shares theoretically created (adjustment entered by the valuer: treasury shares, waiver or simplified merger).`,
    reSoulteCalc: (r, pi, vr, sa, src) => `The gap between the retained ratio and the implied ratio of the exchange is compensated by a cash adjustment: (${r} - ${pi}) × ${vr} (reference value of share A, ${src}) = ${sa} per share B contributed.`,
    valRefSrcAuto: "weighted average of the per-share values of A from the retained criteria",
    valRefSrcSaisie: "value adopted by the valuer",
    reSoulteTot: (st, nb) => `Total cash adjustment: ${st} for ${nb} shares B contributed.`,
    reNoSoulte: "The exchange ratio is aligned with the retained ratio: no cash adjustment is due.",
    reNegSoulte: "The exchange ratio serves B shareholders beyond the retained ratio; no cash adjustment is due, the difference being borne by A shareholders.",
    reAssiette: (nb, nom, base, p) => `Base of the check (French Com. Code, art. L. 236-1): ${nb} shares allotted as consideration for the contribution × ${nom} par value = ${base}; legal cap on the cash adjustment: 10% of this base, i.e. ${p}.`,
    reCtrlOk: (st, p) => `The total cash adjustment (${st}) does not exceed this cap (${p}): the transaction remains within the limit set by article L. 236-1 of the French Commercial Code.`,
    reCtrlKo: (st, p) => `The total cash adjustment (${st}) exceeds this cap (${p}): the transaction is beyond the limit set by article L. 236-1 of the French Commercial Code.`,
    reCtrlNa: "The legal cap check on the cash adjustment (French Com. Code, art. L. 236-1) was not performed, as the par value of share A was not entered.",
    reRompus: (m) => `As the number of B shares is not divisible by ${m}, fractional entitlements will remain and must be settled (purchase or sale of rights).`,
    mention: "Document prepared exclusively from the data and justifications entered by the valuer. No market data or content was generated automatically.",
    footer: "Hard to Value — Exchange-ratio memo",
  },
};

// ── Modèle de blocs du mémo — source unique pour l'aperçu et le PDF ──
const buildMemoModel = (d, M, loc, dateStr) => {
  const dec = (v) => fmtDec(v, loc);
  const eur = (v) => fmtEuro(v, loc);
  const mon = (v) => fmtMoney(v, loc);
  const int = (v) => fmtInt(v, loc);
  const nomA = d.soc.A.nom || "A", nomB = d.soc.B.nom || "B";
  const b = [];
  b.push({ type: "head", title: M.memoTitle, sub: M.memoSub, op: M.op(nomA, nomB), date: `${M.dateLbl} ${dateStr}` });
  b.push({ type: "h", text: M.s1 });
  b.push({ type: "p", text: M.op(nomA, nomB) + "." });
  b.push({ type: "p", text: `A : ${M.socLine(nomA, int(d.nA))} · B : ${M.socLine(nomB, int(d.nB))}` });
  b.push({ type: "h", text: M.s2 });
  b.push({ type: "p", text: M.cadre });
  b.push({ type: "h", text: M.s3 });
  const nEc = d.ecartes.length;
  b.push({ type: "p", text: nEc ? M.demarche(d.retained.length + nEc, d.retained.length, nEc) : M.demarcheNoEc(d.retained.length, d.retained.length) });
  b.push({ type: "h", text: M.s4 });
  b.push({
    type: "table",
    cols: [116, 62, 68, 68, 60, 60, 65],
    head: [M.thCrit, M.thUnit, M.thVA, M.thVB, M.thvA, M.thvB, M.thPar],
    rows: d.retained.map((r) => [
      r.label || "—",
      r.unite === "parAction" ? M.uniteP : M.uniteG,
      r.unite === "parAction" ? eur(r.valA) : mon(r.valA),
      r.unite === "parAction" ? eur(r.valB) : mon(r.valB),
      eur(r.vpaA), eur(r.vpaB), dec(r.parite),
    ]),
  });
  b.push({ type: "note", text: M.poidsNote(d.retained.map((r) => `${r.label || "—"} : ${r.poids > 0 ? r.poids : 0}`).join(" · ")) });
  b.push({ type: "h", text: M.s5 });
  if (!nEc) b.push({ type: "p", text: M.aucunEcarte });
  else d.ecartes.forEach((r) => b.push({ type: "p", text: "— " + M.ecarteLine(r.label || "—", (r.motif || "").trim() || "—") }));
  b.push({ type: "h", text: M.s6 });
  if (d.four) b.push({ type: "p", text: M.fourch(dec(d.four.min), dec(d.four.max), dec(d.four.moy), dec(d.four.pond)) });
  b.push({ type: "h", text: M.s7 });
  b.push({ type: "p", strong: true, text: M.retRv(dec(d.rRet)) });
  if (d.ecartVsPond != null) b.push({ type: "p", text: M.retEcart(!!d.inRange, fmtPct(d.ecartVsPond, loc)) });
  b.push({ type: "p", text: M.retJust });
  b.push({ type: "quote", text: (d.justification || "").trim() || "—" });
  b.push({ type: "h", text: M.s8 });
  if (d.hasRatio) {
    const exact = Math.abs(d.ecartImpl ?? 0) < 1e-9;
    b.push({ type: "p", strong: true, text: exact
      ? M.re1Exact(dec(d.rRet), int(d.N), int(d.M))
      : M.re1(dec(d.rRet), int(d.N), int(d.M), dec(d.pariteImpl), fmtPct(d.ecartImpl, loc)) });
    b.push({ type: "p", text: d.attrAdjusted ? M.reAttrAdj(int(d.titresAttribues), int(d.actionsCreees)) : M.reAttr(int(d.titresAttribues)) });
    if (d.soulteAct != null && d.soulteAct > 1e-9) {
      b.push({ type: "p", text: M.reSoulteCalc(dec(d.rRet), dec(d.pariteImpl), eur(d.valRefA), eur(d.soulteAct), d.valRefIsAuto ? M.valRefSrcAuto : M.valRefSrcSaisie) });
      b.push({ type: "p", text: M.reSoulteTot(mon(d.soulteTot), int(d.nB)) });
    }
    else if (d.soulteAct != null && d.soulteAct < -1e-9) b.push({ type: "p", text: M.reNegSoulte });
    else b.push({ type: "p", text: M.reNoSoulte });
    if (d.ctrl != null) {
      b.push({ type: "p", text: M.reAssiette(int(d.titresAttribues), eur(d.nominalA), mon(d.nominalA * d.titresAttribues), mon(d.plafond)) });
      b.push({ type: "p", text: d.ctrl ? M.reCtrlOk(mon(d.soulteTot), mon(d.plafond)) : M.reCtrlKo(mon(d.soulteTot), mon(d.plafond)) });
    } else if (d.soulteAct != null && d.soulteAct > 1e-9) b.push({ type: "p", text: M.reCtrlNa });
    if (d.rompus) b.push({ type: "p", text: M.reRompus(int(d.M)) });
  }
  b.push({ type: "note", text: M.mention });
  return b;
};

// ── Critères par défaut : les 6 familles doctrinales, librement modifiables ──
const DEFAULT_CRITERES = (fr) => [
  { id: "anr", label: fr ? "ANR (patrimoniale)" : "NAV (asset-based)", statut: "retenu", unite: "globale", valA: null, valB: null, poids: 1, motif: "" },
  { id: "dcf", label: fr ? "DCF (rentabilité)" : "DCF (earnings power)", statut: "retenu", unite: "globale", valA: null, valB: null, poids: 1, motif: "" },
  { id: "rdt", label: fr ? "Rendement (capitalisation)" : "Yield (capitalisation)", statut: "retenu", unite: "globale", valA: null, valB: null, poids: 1, motif: "" },
  { id: "cbr", label: fr ? "Comparables boursiers" : "Listed comparables", statut: "retenu", unite: "globale", valA: null, valB: null, poids: 1, motif: "" },
  { id: "trx", label: fr ? "Transactions comparables" : "Comparable transactions", statut: "retenu", unite: "globale", valA: null, valB: null, poids: 1, motif: "" },
  { id: "crs", label: fr ? "Cours de bourse" : "Share price", statut: "retenu", unite: "parAction", valA: null, valB: null, poids: 1, motif: "" },
];

const PariteFusion = ({ lang }) => {
  const fr = lang === "fr";
  const t = T[fr ? "fr" : "en"];
  const loc = locOf(lang);

  // État de session (scope "parite") — toutes les saisies client survivent à
  // la navigation, jamais au rechargement, jamais sur disque.
  const [soc, setSoc] = useSessionState("societes", { A: { nom: "", nbActions: null }, B: { nom: "", nbActions: null } });
  const [criteres, setCriteres] = useSessionState("criteres", () => DEFAULT_CRITERES(fr));
  const [retenue, setRetenue] = useSessionState("pariteRetenue", { valeur: null, justification: "" });
  const [ech, setEch] = useSessionState("echange", { nbA: null, nbB: null, valRefA: null, nominalA: null, titresAttribues: null });
  const [pTab, setPTab] = useSessionState("pTab", "calc");

  const uSoc = (side, k) => (v) => setSoc((p) => ({ ...p, [side]: { ...p[side], [k]: v } }));
  const uCrit = (id, k) => (v) => setCriteres((p) => p.map((c) => (c.id === id ? { ...c, [k]: v } : c)));
  const delCrit = (id) => setCriteres((p) => p.filter((c) => c.id !== id));
  const addCrit = () =>
    setCriteres((p) => [...p, { id: "c" + Math.random().toString(36).slice(2, 8), label: "", statut: "retenu", unite: "globale", valA: null, valB: null, poids: 1, motif: "" }]);

  const nA = soc.A.nbActions, nB = soc.B.nbActions;

  // ── Normalisation du sens : valeur globale ÷ nb d'actions vs par action ──
  const rows = useMemo(() => criteres.map((c) => {
    const conv = (val, n) => {
      if (val == null || val <= 0) return { v: null, needShares: false };
      if (c.unite === "parAction") return { v: val, needShares: false };
      return n > 0 ? { v: val / n, needShares: false } : { v: null, needShares: true };
    };
    const a = conv(c.valA, nA), b = conv(c.valB, nB);
    const vpaA = a.v, vpaB = b.v;
    const filledA = c.valA != null && c.valA > 0, filledB = c.valB != null && c.valB > 0;
    const complete = vpaA != null && vpaB != null;
    const halfFilled = c.statut === "retenu" && (filledA !== filledB);
    const needShares = c.statut === "retenu" && (a.needShares || b.needShares);
    const parite = complete && c.statut === "retenu" ? vpaB / vpaA : null;
    return { ...c, vpaA, vpaB, parite, halfFilled, needShares, filledAny: filledA || filledB };
  }), [criteres, nA, nB]);

  const retained = useMemo(() => rows.filter((r) => r.statut === "retenu" && r.parite != null), [rows]);
  const ecartesSansMotif = rows.filter((r) => r.statut === "ecarte" && !(r.motif || "").trim());

  // ── Fourchette + moyenne pondérée ──
  const four = useMemo(() => {
    if (!retained.length) return null;
    const ps = retained.map((r) => r.parite);
    const min = Math.min(...ps), max = Math.max(...ps);
    const moy = ps.reduce((s, p) => s + p, 0) / ps.length;
    const sumW = retained.reduce((s, r) => s + (r.poids > 0 ? r.poids : 0), 0);
    const pond = sumW > 0 ? retained.reduce((s, r) => s + (r.poids > 0 ? r.poids : 0) * r.parite, 0) / sumW : null;
    const sumWA = retained.reduce((s, r) => s + (r.poids > 0 ? r.poids : 0), 0);
    const autoValRefA = sumWA > 0 ? retained.reduce((s, r) => s + (r.poids > 0 ? r.poids : 0) * r.vpaA, 0) / sumWA : null;
    return { min, max, moy, pond, autoValRefA };
  }, [retained]);

  const rRet = retenue.valeur;
  const ecartVsPond = rRet != null && four?.pond ? (rRet - four.pond) / four.pond : null;
  const inRange = rRet != null && four ? rRet >= four.min && rRet <= four.max : null;

  // ── Suggestions de rapports praticables (fractions N/M ≤ 12) ──
  const suggestions = useMemo(() => {
    if (rRet == null || rRet <= 0) return [];
    const seen = new Set(); const out = [];
    for (let m = 1; m <= 12; m++) {
      const n = Math.round(rRet * m);
      if (n < 1 || n > 12) continue;
      const g = gcd(n, m); const key = `${n / g}/${m / g}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ n: n / g, m: m / g, val: n / m, ecart: (n / m - rRet) / rRet });
    }
    return out.sort((x, y) => Math.abs(x.ecart) - Math.abs(y.ecart)).slice(0, 4);
  }, [rRet]);

  // ── Rapport d'échange, soulte, contrôle L. 236-1 ──
  const N = ech.nbA, M = ech.nbB;
  const hasRatio = N != null && N >= 1 && M != null && M >= 1;
  const pariteImpl = hasRatio ? N / M : null;
  const actionsCreees = hasRatio && nB > 0 ? (nB * N) / M : null;
  const rompus = hasRatio && nB > 0 && Math.round(nB) % Math.round(M) !== 0;
  const valRefA = ech.valRefA != null && ech.valRefA > 0 ? ech.valRefA : four?.autoValRefA ?? null;
  const EPS = 1e-9;
  const ecartImpl = rRet != null && rRet > 0 && pariteImpl != null ? (pariteImpl - rRet) / rRet : null;
  const soulteAct = rRet != null && pariteImpl != null && valRefA != null ? (rRet - pariteImpl) * valRefA : null;
  const soulteTot = soulteAct != null && nB > 0 ? soulteAct * nB : null;
  // Plafond légal : 10 % de la valeur NOMINALE des titres ATTRIBUÉS en
  // rémunération de l'apport (jamais leur valeur réelle) — C. com.,
  // art. L. 236-1. L'assiette n'est PAS "toutes les actions créées" : en cas
  // d'auto-détention, de fusion-renonciation ou de fusion simplifiée, le
  // nombre attribué diverge du théorique — d'où le champ saisissable,
  // prérempli à la valeur théorique nB×N/M.
  const titresAttribues = ech.titresAttribues != null && ech.titresAttribues > 0 ? ech.titresAttribues : actionsCreees;
  const plafond = ech.nominalA > 0 && titresAttribues != null ? 0.1 * ech.nominalA * titresAttribues : null;
  const ctrl = plafond != null && soulteTot != null && soulteTot > EPS ? soulteTot <= plafond : null;

  const selRatio = (n, m) => setEch((p) => ({ ...p, nbA: n, nbB: m }));
  const g2 = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 12 };
  const g4 = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 };

  // ── Mémo justificatif (lot 2) : checklist bloquante + modèle de blocs ──
  const ML = MEMO_T[fr ? "fr" : "en"];
  // Bloquant : tout critère "retenu" doit être complet — un critère vide ou
  // à moitié saisi disparaîtrait silencieusement du mémo, alors que la
  // doctrine exige que chaque méthode examinée soit appliquée ou écartée
  // avec motif. L'évaluateur complète, écarte ou supprime.
  const nonComplets = rows.filter((r) => r.statut === "retenu" && r.parite == null);
  const ecartes = rows.filter((r) => r.statut === "ecarte");
  const checklist = [
    { ok: !!((soc.A.nom || "").trim() && (soc.B.nom || "").trim() && nA > 0 && nB > 0), label: ML.ckSoc },
    { ok: retained.length >= 1, label: ML.ckCrit },
    { ok: ecartesSansMotif.length === 0, label: ML.ckMotifs },
    { ok: nonComplets.length === 0, label: ML.ckComplets },
    { ok: rRet != null && !!(retenue.justification || "").trim(), label: ML.ckJustif },
    { ok: hasRatio, label: ML.ckRatio },
  ];
  const memoReady = checklist.every((c) => c.ok);
  const attrAdjusted = ech.titresAttribues != null && actionsCreees != null && Math.round(ech.titresAttribues) !== Math.round(actionsCreees);
  const memoBlocks = useMemo(() => {
    if (!memoReady) return null;
    return buildMemoModel(
      { soc, nA, nB, retained, ecartes, four, rRet, ecartVsPond, inRange, justification: retenue.justification, hasRatio, N, M: ech.nbB, pariteImpl, ecartImpl, actionsCreees, titresAttribues, attrAdjusted, valRefA, valRefIsAuto: !(ech.valRefA > 0), soulteAct, soulteTot, plafond, ctrl, rompus, nominalA: ech.nominalA },
      ML, loc, new Date().toLocaleDateString(loc)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoReady, soc, nA, nB, retained, ecartes, four, rRet, ecartVsPond, inRange, retenue.justification, hasRatio, N, ech.nbB, pariteImpl, ecartImpl, actionsCreees, titresAttribues, attrAdjusted, valRefA, soulteAct, soulteTot, plafond, ctrl, rompus, loc]);
  const exportMemo = () => {
    if (!memoBlocks) return;
    const nm = (s, dflt) => ((s || dflt).replace(/\s+/g, "_").replace(/[^\w-]/g, ""));
    downloadPdf(buildMemoPdf(memoBlocks, ML.footer), `HardToValue_Parite_${nm(soc.A.nom, "A")}_${nm(soc.B.nom, "B")}_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const tabBtn = (id, label) => (
    <button onClick={() => setPTab(id)} style={{
      display: "flex", alignItems: "center", gap: 7, padding: "8px 16px",
      background: pTab === id ? "rgba(255,255,255,0.06)" : "transparent",
      border: "1px solid " + (pTab === id ? P.accentBorder : "rgba(255,255,255,0.08)"),
      borderRadius: 9, color: pTab === id ? P.accent : P.textMuted,
      fontSize: 12.5, fontWeight: pTab === id ? 700 : 500, cursor: "pointer",
    }}>{label}</button>
  );

  return (
    <div>
      <p style={{ fontSize: 12.5, color: P.textMuted, lineHeight: 1.6, margin: "0 0 14px", maxWidth: 860 }}>{t.intro}</p>
      <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
        {tabBtn("calc", "⚖️ " + ML.tabCalc)}
        {tabBtn("memo", "📄 " + ML.tabMemo)}
      </div>

      {pTab === "calc" && (<>

      {/* ── 1. Sociétés ── */}
      <SecT n="01" t={t.s1} />
      <div style={g2}>
        {["A", "B"].map((side) => (
          <div key={side} style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: side === "A" ? P.accent : P.textSec, background: side === "A" ? P.accentSoft : "rgba(255,255,255,0.04)", display: "inline-block", padding: "3px 10px", borderRadius: 5, marginBottom: 12, letterSpacing: "0.04em" }}>
              {side === "A" ? t.absorbante : t.absorbee}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 10 }}>
              <div>
                <label style={lbS}>{t.denomination}</label>
                <input value={soc[side].nom} placeholder={t.denomPh} onChange={(e) => uSoc(side, "nom")(e.target.value)} style={{ ...inS, fontFamily: "inherit" }} />
              </div>
              <div>
                <label style={lbS}>{t.nbActions}</label>
                <NumIn v={soc[side].nbActions} oc={uSoc(side, "nbActions")} fmt={(v) => fmtInt(v, loc)} ph="ex. 1 000 000" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── 2. Critères ── */}
      <SecT n="02" t={t.s2} />
      <p style={{ fontSize: 11.5, color: P.textMuted, margin: "0 0 10px", lineHeight: 1.5 }}>{t.critHint}</p>
      <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "8px 12px 12px", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 880 }}>
          <thead>
            <tr>
              <th style={{ ...thS, textAlign: "left", width: "17%" }}>{t.colCrit}</th>
              <th style={{ ...thS, textAlign: "left", width: "13%" }}>{t.colUnit}</th>
              <th style={{ ...thS, width: "11%" }}>{t.colValA}</th>
              <th style={{ ...thS, width: "11%" }}>{t.colValB}</th>
              <th style={{ ...thS, width: "10%" }}>{t.colVpaA}</th>
              <th style={{ ...thS, width: "10%" }}>{t.colVpaB}</th>
              <th style={{ ...thS, width: "11%" }}>{t.colParite}</th>
              <th style={{ ...thS, width: "7%" }}>{t.colPoids}</th>
              <th style={{ ...thS, width: "10%" }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const off = r.statut === "ecarte";
              return (
                <React.Fragment key={r.id}>
                  <tr style={{ opacity: off ? 0.55 : 1 }}>
                    <td style={{ ...tdS, textAlign: "left" }}>
                      <input value={r.label} placeholder={fr ? "Nom du critère" : "Criterion name"} onChange={(e) => uCrit(r.id, "label")(e.target.value)} style={{ ...inS, fontFamily: "inherit", fontSize: 12.5 }} />
                    </td>
                    <td style={{ ...tdS, textAlign: "left" }}>
                      <select value={r.unite} disabled={off} onChange={(e) => uCrit(r.id, "unite")(e.target.value)} style={{ ...inS, fontFamily: "inherit", fontSize: 11.5, cursor: "pointer", opacity: off ? 0.35 : 1 }}>
                        <option value="globale">{t.uGlobale}</option>
                        <option value="parAction">{t.uParAction}</option>
                      </select>
                    </td>
                    <td style={tdS}><NumIn v={r.valA} oc={uCrit(r.id, "valA")} disabled={off} fmt={(v) => (r.unite === "parAction" ? fmtEuro(v, loc) : fmtMoney(v, loc))} ph={r.unite === "parAction" ? t.phParAction : t.phGlobale} /></td>
                    <td style={tdS}><NumIn v={r.valB} oc={uCrit(r.id, "valB")} disabled={off} fmt={(v) => (r.unite === "parAction" ? fmtEuro(v, loc) : fmtMoney(v, loc))} ph={r.unite === "parAction" ? t.phParAction : t.phGlobale} /></td>
                    <td style={{ ...tdS, color: P.textSec }}>{off ? "—" : fmtEuro(r.vpaA, loc)}</td>
                    <td style={{ ...tdS, color: P.textSec }}>{off ? "—" : fmtEuro(r.vpaB, loc)}</td>
                    <td style={{ ...tdS, color: P.accent, fontWeight: 700 }}>{off ? "—" : fmtDec(r.parite, loc)}</td>
                    <td style={tdS}><NumIn v={r.poids} oc={uCrit(r.id, "poids")} disabled={off} fmt={(v) => String(v)} ph="1" /></td>
                    <td style={{ ...tdS, whiteSpace: "nowrap" }}>
                      <button onClick={() => uCrit(r.id, "statut")(off ? "retenu" : "ecarte")}
                        style={{ background: off ? "rgba(255,255,255,0.05)" : "rgba(34,197,94,0.12)", border: `1px solid ${off ? "rgba(255,255,255,0.12)" : "rgba(34,197,94,0.35)"}`, borderRadius: 6, padding: "4px 9px", color: off ? P.textMuted : "#22c55e", fontSize: 10.5, fontWeight: 700, cursor: "pointer", marginRight: 6 }}>
                        {off ? t.ecarte : t.retenu}
                      </button>
                      <button onClick={() => delCrit(r.id)} title={t.delCrit}
                        style={{ background: "transparent", border: "none", color: P.textMuted, fontSize: 13, cursor: "pointer", padding: 2 }}>✕</button>
                    </td>
                  </tr>
                  {off && (
                    <tr>
                      <td colSpan={9} style={{ padding: "0 8px 10px", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                        <input value={r.motif} placeholder={t.motifPh} onChange={(e) => uCrit(r.id, "motif")(e.target.value)}
                          style={{ ...inS, fontFamily: "inherit", fontSize: 12, fontStyle: "italic", borderColor: (r.motif || "").trim() ? "rgba(255,255,255,0.08)" : "rgba(251,191,36,0.35)" }} />
                        {!(r.motif || "").trim() && <div style={{ fontSize: 11, color: "#fbbf24", marginTop: 4 }}>{t.motifMissing}</div>}
                      </td>
                    </tr>
                  )}
                  {!off && (r.halfFilled || r.needShares) && (
                    <tr>
                      <td colSpan={9} style={{ padding: "0 8px 8px", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                        <div style={{ fontSize: 11, color: "#fbbf24" }}>{r.needShares ? t.needShares : t.incomplete}</div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        <button onClick={addCrit} style={{ marginTop: 10, background: "rgba(255,255,255,0.04)", border: "1px dashed rgba(255,255,255,0.15)", borderRadius: 8, padding: "7px 14px", color: P.textSec, fontSize: 12, cursor: "pointer" }}>
          {t.addCrit}
        </button>
      </div>

      {/* ── 3. Fourchette ── */}
      <SecT n="03" t={`${t.s3} ${four ? "· " + t.nRetenus(retained.length) : ""}`} />
      {!four ? (
        <p style={{ fontSize: 12.5, color: P.textMuted }}>{t.noRetained}</p>
      ) : (
        <>
          <div style={g4}>
            <Metric lb={t.min} v={fmtDec(four.min, loc)} a="#64748b" />
            <Metric lb={t.max} v={fmtDec(four.max, loc)} a="#64748b" />
            <Metric lb={t.moy} v={fmtDec(four.moy, loc)} a="#8b5cf6" />
            <Metric lb={t.pond} v={fmtDec(four.pond, loc)} a={P.accent} />
          </div>
          {retained.length >= 2 && four.max > four.min && (
            <div style={{ position: "relative", height: 92, margin: "20px 10px 0" }}>
              <div style={{ position: "absolute", top: 42, left: 0, right: 0, height: 2, background: "rgba(255,255,255,0.12)" }} />
              {retained.map((r, i) => {
                const x = ((r.parite - four.min) / (four.max - four.min)) * 100;
                const top = i % 2 === 0;
                return (
                  <React.Fragment key={r.id}>
                    <div style={{ position: "absolute", top: 38, left: `${x}%`, width: 10, height: 10, borderRadius: "50%", background: "#64748b", transform: "translateX(-50%)" }} />
                    <div style={{ position: "absolute", top: top ? 12 : 58, left: `${x}%`, transform: "translateX(-50%)", fontSize: 10.5, color: P.textSec, whiteSpace: "nowrap" }}>
                      {r.label || "—"} <span style={{ fontFamily: "monospace", color: P.text }}>{fmtDec(r.parite, loc)}</span>
                    </div>
                  </React.Fragment>
                );
              })}
              {four.pond != null && (
                <>
                  <div style={{ position: "absolute", top: 30, left: `${((four.pond - four.min) / (four.max - four.min)) * 100}%`, width: 2, height: 26, background: P.accent, transform: "translateX(-50%)" }} />
                  <div style={{ position: "absolute", top: 76, left: `${((four.pond - four.min) / (four.max - four.min)) * 100}%`, transform: "translateX(-50%)", fontSize: 10.5, color: P.accent, fontWeight: 700, whiteSpace: "nowrap" }}>
                    {t.pondMark} {fmtDec(four.pond, loc)}
                  </div>
                </>
              )}
              {rRet != null && rRet >= four.min && rRet <= four.max && (
                <div style={{ position: "absolute", top: 30, left: `${((rRet - four.min) / (four.max - four.min)) * 100}%`, width: 2, height: 26, background: "#22c55e", transform: "translateX(-50%)" }} />
              )}
            </div>
          )}
        </>
      )}

      {/* ── 4. Parité retenue ── */}
      <SecT n="04" t={t.s4} />
      <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 16 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ width: 130 }}>
            <label style={lbS}>{t.retVal}</label>
            <NumIn v={retenue.valeur} oc={(v) => setRetenue((p) => ({ ...p, valeur: v }))} fmt={(v) => fmtDec(v, loc)} ph="ex. 1,22" style={{ fontSize: 15, padding: "9px 10px" }} />
          </div>
          {four?.pond != null && (
            <button onClick={() => setRetenue((p) => ({ ...p, valeur: four.pond }))}
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "9px 14px", color: P.textSec, fontSize: 12, cursor: "pointer" }}>
              {t.takePond(fmtDec(four.pond, loc))}
            </button>
          )}
          {rRet != null && four && (
            <div style={{ fontSize: 12, paddingBottom: 9, color: inRange ? "#22c55e" : "#fbbf24" }}>
              {inRange ? "✓ " : "⚠ "}{inRange ? t.inRange : t.outRange}
              {ecartVsPond != null && <span style={{ color: P.textMuted }}> · {t.ecartPond(fmtPct(ecartVsPond, loc))}</span>}
            </div>
          )}
        </div>
        <div style={{ marginTop: 14 }}>
          <label style={lbS}>{t.justif}</label>
          <textarea value={retenue.justification} placeholder={t.justifPh} onChange={(e) => setRetenue((p) => ({ ...p, justification: e.target.value }))}
            style={{ ...inS, fontFamily: "inherit", fontSize: 12.5, minHeight: 64, resize: "vertical", lineHeight: 1.5 }} />
        </div>
        {rRet != null && (
          <div style={{ marginTop: 12, background: "rgba(255,255,255,0.03)", borderRadius: 8, padding: "10px 14px", fontSize: 13 }}>
            <span style={{ color: P.textMuted }}>{t.rv} : </span>
            <span style={{ fontFamily: "monospace", fontWeight: 700, color: P.text }}>{t.rvText(fmtDec(rRet, loc))}</span>
          </div>
        )}
      </div>

      {/* ── 5. Rapport d'échange & soulte ── */}
      <SecT n="05" t={t.s5} />
      <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 16 }}>
        <label style={lbS}>{t.sugg}</label>
        {rRet == null ? (
          <p style={{ fontSize: 12, color: P.textMuted, margin: "4px 0 0" }}>{t.suggNeedsRet}</p>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "6px 0 4px" }}>
            {suggestions.map((s) => {
              const sel = N === s.n && M === s.m;
              return (
                <button key={`${s.n}/${s.m}`} onClick={() => selRatio(s.n, s.m)}
                  style={{ background: sel ? P.accentSoft : "rgba(255,255,255,0.04)", border: `1px solid ${sel ? P.accentBorder : "rgba(255,255,255,0.1)"}`, borderRadius: 18, padding: "6px 13px", color: sel ? P.accent : P.textSec, fontSize: 12, fontFamily: "monospace", cursor: "pointer", fontWeight: sel ? 700 : 400 }}>
                  {t.reText(s.n, s.m)} · {fmtDec(s.val, loc)} · {t.ecartLbl} {fmtPct(s.ecart, loc)}
                </button>
              );
            })}
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10, marginTop: 12 }}>
          <div><label style={lbS}>{t.nbA}</label><NumIn v={ech.nbA} oc={(v) => setEch((p) => ({ ...p, nbA: v }))} fmt={(v) => fmtInt(v, loc)} ph="ex. 6" /></div>
          <div><label style={lbS}>{t.nbB}</label><NumIn v={ech.nbB} oc={(v) => setEch((p) => ({ ...p, nbB: v }))} fmt={(v) => fmtInt(v, loc)} ph="ex. 5" /></div>
          <div>
            <label style={lbS}>{t.valRef}</label>
            <NumIn v={ech.valRefA} oc={(v) => setEch((p) => ({ ...p, valRefA: v }))} fmt={(v) => fmtEuro(v, loc)} ph={four?.autoValRefA != null ? fmtEuro(four.autoValRefA, loc) : "—"} />
            {ech.valRefA == null && four?.autoValRefA != null && (
              <div style={{ fontSize: 10.5, color: P.textMuted, marginTop: 3, lineHeight: 1.4 }}>{t.valRefAuto(fmtEuro(four.autoValRefA, loc))}</div>
            )}
          </div>
          <div>
            <label style={lbS}>{t.nominal}</label>
            <NumIn v={ech.nominalA} oc={(v) => setEch((p) => ({ ...p, nominalA: v }))} fmt={(v) => fmtEuro(v, loc)} ph="ex. 50,00" />
            <div style={{ fontSize: 10.5, color: P.textMuted, marginTop: 3 }}>{t.nominalHint}</div>
          </div>
          <div>
            <label style={lbS}>{t.titresAttr}</label>
            <NumIn v={ech.titresAttribues} oc={(v) => setEch((p) => ({ ...p, titresAttribues: v }))} fmt={(v) => fmtInt(v, loc)} ph={actionsCreees != null ? fmtInt(actionsCreees, loc) : "—"} />
            {ech.titresAttribues == null && actionsCreees != null && (
              <div style={{ fontSize: 10.5, color: P.textMuted, marginTop: 3, lineHeight: 1.4 }}>{t.titresAttrAuto(fmtInt(actionsCreees, loc))}</div>
            )}
          </div>
        </div>

        {hasRatio && rRet != null && (
          <>
            <div style={{ ...g4, marginTop: 14 }}>
              <Metric lb={t.crees} v={fmtInt(actionsCreees, loc)} a="#06b6d4" />
              <Metric lb={t.pariteImpl} v={fmtDec(pariteImpl, loc)} a="#8b5cf6" />
              <Metric lb={t.soulteAct} v={soulteAct != null && soulteAct > EPS ? fmtEuro(soulteAct, loc) : "—"} a="#f59e0b" />
              <Metric lb={t.soulteTot} v={soulteTot != null && soulteTot > EPS ? fmtMoney(soulteTot, loc) : "—"} a="#f59e0b" />
            </div>
            {rompus && <WarnLine>{t.rompus(fmtInt(M, loc))}</WarnLine>}
            {soulteAct != null && Math.abs(soulteAct) <= EPS && <WarnLine tone="ok">{t.soulteNone}</WarnLine>}
            {soulteAct != null && soulteAct < -EPS && <WarnLine>{t.soulteNeg}</WarnLine>}
            {ctrl != null && (
              <WarnLine tone={ctrl ? "ok" : "err"}>
                {ctrl ? t.ctrlOk(fmtMoney(soulteTot, loc), fmtMoney(plafond, loc)) : t.ctrlKo(fmtMoney(soulteTot, loc), fmtMoney(plafond, loc))}
              </WarnLine>
            )}
            <div style={{ marginTop: 14, background: "rgba(255,255,255,0.03)", border: `1px solid ${P.accentBorder}`, borderRadius: 10, padding: "12px 16px", fontSize: 13, lineHeight: 1.8 }}>
              <span style={{ color: P.textMuted }}>{t.rv} : </span>
              <span style={{ fontFamily: "monospace", fontWeight: 700 }}>{t.rvText(fmtDec(rRet, loc))}</span>
              <br />
              <span style={{ color: P.textMuted }}>{t.re} : </span>
              <span style={{ fontFamily: "monospace", fontWeight: 700 }}>{t.reText(fmtInt(N, loc), fmtInt(M, loc))}</span>
              {soulteAct != null && soulteAct > EPS && (
                <span style={{ color: P.textSec }}>{fr ? ", soulte de " : ", cash adjustment of "}<span style={{ fontFamily: "monospace", fontWeight: 700, color: P.text }}>{fmtEuro(soulteAct, loc)}</span>{fr ? " par action B" : " per B share"}</span>
              )}
            </div>
          </>
        )}
        {ecartesSansMotif.length > 0 && (
          <WarnLine>
            {fr
              ? `${ecartesSansMotif.length} critère(s) écarté(s) sans motif — la justification des critères écartés est une exigence doctrinale (repris dans le mémo).`
              : `${ecartesSansMotif.length} discarded criteri${ecartesSansMotif.length > 1 ? "a" : "on"} without a reason — justifying discarded criteria is a doctrinal requirement (quoted in the memo).`}
          </WarnLine>
        )}
      </div>

      </>)}

      {pTab === "memo" && (
        <div style={{ marginTop: 18 }}>
          <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 16, marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: P.textSec, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>{ML.ckTitle}</div>
            {checklist.map((c, i) => (
              <div key={i} style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 12.5, lineHeight: 1.9, color: c.ok ? P.textSec : "#fbbf24" }}>
                <span style={{ color: c.ok ? "#22c55e" : "#fbbf24", fontWeight: 700 }}>{c.ok ? "✓" : "✗"}</span>{c.label}
              </div>
            ))}
            {memoReady && ech.nominalA == null && soulteAct != null && soulteAct > EPS && <div style={{ fontSize: 11.5, color: P.textMuted, marginTop: 8 }}>ⓘ {ML.ckNominal}</div>}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14 }}>
              <button onClick={exportMemo} disabled={!memoReady} style={{
                background: memoReady ? P.accent : "rgba(201,168,76,0.25)", border: "none", borderRadius: 9,
                padding: "10px 18px", color: memoReady ? P.accentText : P.textMuted, fontSize: 13, fontWeight: 700,
                cursor: memoReady ? "pointer" : "not-allowed",
              }}>{ML.exportBtn}</button>
              {!memoReady && <span style={{ fontSize: 11.5, color: P.textMuted }}>{ML.exportBlocked}</span>}
            </div>
          </div>
          {memoBlocks && <MemoPaper blocks={memoBlocks} />}
        </div>
      )}
    </div>
  );
};

export default PariteFusion;
